import {
  CommonService,
  Exception,
  StorageService,
  StorageType,
  ToasterService,
  Validator,
} from '@anddone/ui-utilities';
import { Component, signal, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ValidationConstant } from '../../../constants/validation.constant';
import { LoginService } from '../../../services/login-service';
import { UserTypeEnum } from '../../../enum/user-type.enum';
import { MessageConstants } from '../../../constants/message.constant';
import moment from 'moment';
import { AppSetting } from '../../../constants/appsetting.constant';
import { UserService } from '../../../services/user-service';
import { SettingsService } from '../../../services/settings-service';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-login',
  imports: [CommonModule, TranslateModule, ReactiveFormsModule],
  templateUrl: './login.html',
  styleUrl: './login.scss',
})
export class Login implements OnInit {
  loginForm!: FormGroup;
  loginFormErrors: any = {};
  validator: Validator;
  isLoader = signal<boolean>(false);
  loggedInUser: any;
  userDetailsData: any = {};
  currentYear: number = new Date().getFullYear();
  appSetting = AppSetting;

  config = {
    userName: {
      required: { name: ValidationConstant.login.username.name },
      maxlength: {
        name: ValidationConstant.login.username.name,
        max: ValidationConstant.login.username.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.login.username.name,
        min: ValidationConstant.login.username.minLength.toString(),
      },
    },
    password: {
      required: { name: ValidationConstant.login.password.name },
      maxlength: {
        name: ValidationConstant.login.password.name,
        max: ValidationConstant.login.password.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.login.password.name,
        min: ValidationConstant.login.password.minLength.toString(),
      },
    },
  };
  routeParameter: any;
  passwordFieldType = 'password';
  specificFeature: any;

  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private storageService = inject(StorageService);
  private toasterService = inject(ToasterService);
  private commonService = inject(CommonService);
  private userService = inject(UserService);
  private settingsService = inject(SettingsService);
  constructor() {
    this.validator = new Validator(this.config);
  }

  ngOnInit(): void {
    sessionStorage.clear();
    this.loginForm = this.formBuilder.group({
      userName: ['', [Validators.required]],
      password: ['', [Validators.required]],
      rememberMe: [null, []],
    });

    this.loginForm.valueChanges.subscribe((data) => this.onValueChanged(data));

    // get username from cookie
    if (this.storageService.get(StorageType.Cookie, 'userAuth')) {
      const data = this.storageService.get(StorageType.Cookie, 'userAuth');
      this.loggedInUser = data ? JSON.parse(data) : null;
      this.loginForm.controls['userName'].patchValue(this.loggedInUser);
    }
  }

  triggerInput(event: any) {
    if (event.keyCode == 13) {
      this.login();
    }
    return;
  }

  // form on change value
  onValueChanged(_data?: any) {
    if (!this.loginForm) {
      return;
    }
    this.loginFormErrors = this.validator.validate(this.loginForm);
  }

  // submit login api call
  login() {
    this.validator.validateAllFormFields(this.loginForm);
    this.loginFormErrors = this.validator.validate(this.loginForm);
    if (this.loginForm?.invalid) {
      return;
    }

    this.settingsService.isSessionExpiredCheck = false;
    const reqObj = {
      userName: this.loginForm?.value.userName,
      password: this.loginForm?.value.password,
      userType: UserTypeEnum.ALLY,
    };

    this.isLoader.set(true);
    this.loginService.login(reqObj)?.subscribe({
      next: (res: any) => {
        res.adminUser = reqObj.userName;
        this.handleLoginResponse(res);
        this.commonService.getAllCountryList();
        this.commonService.getStateList();
      },
      error: (e) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  // handle login screen, dashboard or change password
  handleLoginResponse(loginResponse: any) {
    if (
      loginResponse.role !== null &&
      loginResponse.role != '' &&
      loginResponse.changePassword === false
    ) {
      this.toasterService.showError(MessageConstants.login.loginPermission, 'full');
      return;
    }

    const userName = this.loginForm.value.userName
      ? this.loginForm.value.userName
      : loginResponse.userName;
    if (loginResponse['changePassword'] === true) {
      this.storageService.save(
        StorageType.Session,
        'userOldPwd',
        JSON.stringify(this.loginForm.value.password)
      );
      this.router.navigate([
        '/change-password',
        loginResponse['parentId'],
        loginResponse['userType'],
        userName,
      ]);
    } else {
      this.storageService.save(StorageType.Session, 'auth', JSON.stringify(loginResponse));
      this.storageService.save(
        StorageType.Session,
        'refreshToken',
        JSON.stringify(loginResponse.refreshToken)
      );
      this.storageService.save(
        StorageType.Session,
        'refreshTokenExpireTime',
        moment().add(AppSetting.refreshTokenRenewTime, 'milliseconds').valueOf().toString()
      );
      this.storageService.save(StorageType.Session, 'timeOut', JSON.stringify(false));

      this.loginService.getloggedUsersDetails(loginResponse).subscribe({
        next: (res: any) => {
          if (this.loginForm.value['rememberMe'] === true) {
            this.storageService.save(
              StorageType.Cookie,
              'userAuth',
              JSON.stringify(this.loginForm.value.userName)
            );
          }
          this.storageService.save(StorageType.Session, 'userDetails', JSON.stringify(res));
          if (loginResponse.userId?.length > 0) {
            this.nonDefaultAdminUserDetails(loginResponse);
          } else {
            this.isLoader.set(false);
            this.toasterService.showSuccess(MessageConstants.login.loginSuccess, 'full');
            if (this.storageService.previousPageUrl) {
              const previousPageUrl = this.storageService.previousPageUrl;
              // Clear the previous page URL from the service
              this.storageService.previousPageUrl = null;
              // Navigate to the previous page URL
              this.router.navigateByUrl(previousPageUrl);
            } else {
              this.router.navigate(['/ally/users']);
            }
          }
        },
        error: (e) => {
          this.isLoader.set(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
    }
  }

  nonDefaultAdminUserDetails(res: any) {
    const reqObj = {
      allyId: res.parentId,
      userId: res.userId,
    };
    //call API to get user details by ID
    this.userDetailsData = this.userService.getUserDetailsById(reqObj).subscribe({
      next: (response: any) => {
        this.storageService.save(
          StorageType.Session,
          'nonDefaultAdminUser',
          JSON.stringify(response)
        );
        this.isLoader.set(false);
        this.toasterService.showSuccess(MessageConstants.login.loginSuccess, 'full');
        this.router.navigate(['/ally/users']);
      },
      error: (error: any) => {
        const toastMessage = Exception.exceptionMessage(error);
        this.toasterService.showError(toastMessage.join(', '), 'full');
        this.isLoader.set(false);
      },
    });
  }

  forgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }

  //make password visible
  onTogglePassword() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }
}
